import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { Exact } from "@Helpers/types/exact";
import type {
  DatabankViewData,
  DatabankState,
  SetDatabankPayload,
  ResetDatabankPayload,
} from "@ServerTypes/queries/queries.schema";

const initialState: DatabankState = {};

const databankSlice = createSlice({
  name: "databank",
  initialState,
  reducers: {
    setDatabankData: (state: DatabankState, action: PayloadAction<SetDatabankPayload>) => {
      const { id, fileVersionId, data } = action.payload;
      if (!state[id]) state[id] = {};
      state[id][fileVersionId] = {
        ...(state[id][fileVersionId] || {}),
        ...data,
      };
    },
    updateCurrentDatabankView: (state: DatabankState, action: PayloadAction<SetDatabankPayload>) => {
      const { id, fileVersionId, data } = action.payload;
      const currentSelectedView = state[id]?.[fileVersionId]?.selectedView;
      if (!currentSelectedView) return;
      if (!state[id]) state[id] = {};
      const currentFileVersion = state[id][fileVersionId] ?? {};
      state[id][fileVersionId] = {
        ...currentFileVersion,
        views: {
          ...currentFileVersion.views,
          [currentSelectedView]: {
            ...(currentFileVersion.views?.[currentSelectedView] ?? {}),
            ...data,
          } as DatabankViewData,
        },
      };
    },
    resetDatabank: (state: DatabankState, action: PayloadAction<ResetDatabankPayload>) => {
      const { id, fileVersionId } = action.payload;
      if (!state[id]) state[id] = {};
      state[id][fileVersionId] = {};
    },
  },
});

const {
  setDatabankData: _setDatabankDataRaw,
  updateCurrentDatabankView: _updateCurrentDatabankViewRaw,
  resetDatabank: _resetDatabankRaw,
} = databankSlice.actions;

/**
 * Object-payload action creators are wrapped with `Exact<>` so excess properties
 * are caught even when the payload is passed through a variable.
 */
export const setDatabankData = <T extends Exact<SetDatabankPayload, T>>(payload: T) => _setDatabankDataRaw(payload);
export const updateCurrentDatabankView = <T extends Exact<SetDatabankPayload, T>>(payload: T) =>
  _updateCurrentDatabankViewRaw(payload);
export const resetDatabank = <T extends Exact<ResetDatabankPayload, T>>(payload: T) => _resetDatabankRaw(payload);

export const Databank = databankSlice.reducer;
export default databankSlice.reducer;
